import classNames from 'classnames'
import { useFormikContext } from 'formik'
import { useEffect, useRef, useState } from 'react'

import type { ReactElement, UIEvent } from 'react'
import type { IPlaceholderFormValue, IPageContainerFormValues } from '~/types/template'

import EmbeddedUiInputs from './Inputs/EmbeddedUiInputs'
import TextInput from './Inputs/TextInput'

import AssetInput from '~/components/Inputs/Formik/AssetInput'
import ColorInput from '~/components/Inputs/Formik/ColorInput'
import IapInput from '~/components/Inputs/Formik/IapInput'
import TranslationInput from '~/components/Inputs/Formik/TranslationInput'
import { debounce } from '~/utils/function'

import './styles.scss'

const Input = ({
  disabled,
  fieldName,
  hiddenFields,
  iapKey,
  placeholderKey,
  rules,
  type,
}: IPlaceholderFormValue & {
  disabled?: boolean
  fieldName: string
  placeholderKey: string
}): ReactElement | null => {
  if (hiddenFields?.includes(type)) {
    return null
  }

  const commonProps = {
    disabled,
    name: fieldName,
  }

  switch (type) {
    case 'translation':
      return <TranslationInput {...commonProps} iapKey={iapKey} rules={rules} />
    case 'color':
      return <ColorInput {...commonProps} />
    case 'asset':
      return <AssetInput {...commonProps} rules={rules} />
    case 'action':
      return <TextInput {...commonProps} placeholder="Action target" />
    case 'iap':
      return <IapInput {...commonProps} placeholderKey={placeholderKey} />
    case 'embeddedUi':
      return <EmbeddedUiInputs {...commonProps} placeholderKey={placeholderKey} />
    default:
      return <TextInput {...commonProps} />
  }
}

interface Props {
  className?: string
  onScroll?: (placeholderKey: string) => void
  placeholders?: IPlaceholderFormValue[]
}

const PlaceholderForm = ({ className, onScroll, placeholders = [] }: Props): ReactElement => {
  const ref = useRef<HTMLInputElement>(null)

  const [isScrolling, setIsScrolling] = useState(false)

  const {
    setFieldValue,
    values: { focus, isDisabled },
  } = useFormikContext<IPageContainerFormValues>()

  const placeholdersGroupedByKey: Map<string, IPlaceholderFormValue[]> = placeholders.reduce((prev, curr) => {
    const foundFormValues = prev.get(curr.key)
    if (foundFormValues) {
      prev.set(curr.key, [...foundFormValues, curr])
    } else {
      prev.set(curr.key, [curr])
    }
    return prev
  }, new Map())

  const handleScroll = debounce((e: UIEvent) => {
    const minY = ref.current?.getBoundingClientRect().y || 0
    const children = (e.target as HTMLInputElement).children

    let focus = ''
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      const childY = child.getBoundingClientRect().y
      if (childY < minY) {
        continue
      } else {
        focus = child.id.split('-')[2]
        break
      }
    }
    if (!isScrolling) {
      onScroll && onScroll(focus)
    }
  }, 50)

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (focus) {
      setIsScrolling(true)
      document.getElementById(`input-group-${focus}`)?.scrollIntoView({ behavior: 'smooth' })
      timeout = setTimeout(() => setIsScrolling(false), 2000)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [focus])

  return (
    <div className={classNames('placeholder-form-component', className)} onScroll={handleScroll} ref={ref}>
      {Array.from(placeholdersGroupedByKey).map(([key, placeholders]) => (
        <div
          className={classNames('inputs-container', { focus: focus === key })}
          id={`input-group-${key}`}
          key={`input-group-${key}`}
          onClick={() => setFieldValue('focus', key)}
        >
          <div className="inputs-container__title">{key}</div>
          <div className="inputs-container__fields">
            {placeholders.map((placeholder, index) => (
              <Input
                {...placeholder}
                disabled={isDisabled}
                key={`${placeholder.key}-${index}`}
                placeholderKey={placeholder.key}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default PlaceholderForm
